import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Crown,
  Shield,
  User,
  MoreVertical,
  MessageSquare,
  Mail,
  UserMinus,
  Pencil,
  Send,
  Copy,
  Link2,
} from 'lucide-react';
import { useLeague } from '@/contexts/LeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLeagueChat } from '@/hooks/useLeagueChat';
import { supabase, SUPABASE_PROJECT_ID } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';

interface MembersTabProps {
  isCommissioner: boolean;
  leagueId: string;
}

const MembersTab = ({ isCommissioner, leagueId }: MembersTabProps) => {
  const { members, activeLeague, refreshLeague } = useLeague();
  const { user } = useAuth();
  const { sendDirectMessage } = useLeagueChat();
  const { toast } = useToast();

  const [removeTarget, setRemoveTarget] = useState<typeof members[0] | null>(null);
  const [editTarget, setEditTarget] = useState<typeof members[0] | null>(null);
  const [editTeamName, setEditTeamName] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [dmTarget, setDmTarget] = useState<typeof members[0] | null>(null);
  const [dmContent, setDmContent] = useState('');
  const [dmSending, setDmSending] = useState(false);
  const [memberEmails, setMemberEmails] = useState<Map<string, string>>(new Map());
  const [emailsLoaded, setEmailsLoaded] = useState(false);

  // Fetch member emails for commissioner
  const fetchEmails = useCallback(async () => {
    if (!isCommissioner || !leagueId) return;
    try {
      const { data, error } = await supabase.functions.invoke('league-member-emails', {
        body: { league_id: leagueId },
      });
      console.log('[MembersTab] email fetch response:', error ? `error: ${error.message}` : 'ok', data);
      if (error) {
        console.error('[MembersTab] invoke error:', error);
        return;
      }
      if (data?.emails) {
        setMemberEmails(new Map(data.emails.map((e: { user_id: string; email: string }) => [e.user_id, e.email])));
      }
    } catch (err) {
      console.error('[MembersTab] failed to fetch emails:', err);
    } finally {
      setEmailsLoaded(true);
    }
  }, [isCommissioner, leagueId]);

  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  const leagueName = activeLeague?.name || 'Sabotage Fantasy';
  const emailSubject = encodeURIComponent(`[${leagueName}] `);

  const handleEmailLeague = () => {
    const allEmails = Array.from(memberEmails.values()).filter(Boolean);
    if (allEmails.length === 0) {
      toast({ title: 'No emails available', description: 'Could not load member emails.', variant: 'destructive' });
      return;
    }
    const bcc = allEmails.join(',');
    window.location.href = `mailto:?bcc=${bcc}&subject=${emailSubject}`;
  };

  const handleEmailMember = (userId: string) => {
    const email = memberEmails.get(userId);
    if (!email) {
      toast({ title: 'Email not available', description: 'Could not load this member\'s email.', variant: 'destructive' });
      return;
    }
    window.location.href = `mailto:${email}?subject=${emailSubject}`;
  };

  const sortedMembers = [...members].sort((a, b) => {
    const roleOrder: Record<string, number> = { commissioner: 0, 'co-commissioner': 1, player: 2 };
    return (roleOrder[a.role] ?? 2) - (roleOrder[b.role] ?? 2);
  });

  const inviteCode = activeLeague?.invite_code || '';
  // SAB-642: share the public MLB join deep link (parity with NFL /nfl/join/:code). Carries the
  // invite through sign-up + email confirmation. The old /auth?invite=CODE links still work —
  // Auth.tsx redirects them here — but new links use the canonical path.
  const inviteLink = `${window.location.origin}/beta/join/${inviteCode}`;

  const handleCopyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      trackEvent('league_invite_sent', { league_id: activeLeague?.id || '', league_name: activeLeague?.name || '' });
      toast({ title: 'Copied!', description: 'Invite link copied to clipboard.' });
    } catch {
      toast({ title: 'Copy failed', description: 'Could not copy to clipboard.', variant: 'destructive' });
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    const { error } = await supabase
      .from('league_members')
      .delete()
      .eq('id', removeTarget.id);
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Member removed', description: `${removeTarget.display_name} has been removed.` });
      await refreshLeague();
    }
    setRemoveTarget(null);
  };

  const handleEditSave = async () => {
    if (!editTarget || !editTarget.team_id || !editTeamName.trim()) return;
    setEditSaving(true);
    try {
      const { error } = await supabase
        .from('teams')
        .update({ name: editTeamName.trim() })
        .eq('id', editTarget.team_id);
      if (error) throw error;
      // Update denormalized matchup names
      await Promise.all([
        supabase.from('matchups').update({ home_team_name: editTeamName.trim() }).eq('home_team_id', editTarget.team_id),
        supabase.from('matchups').update({ away_team_name: editTeamName.trim() }).eq('away_team_id', editTarget.team_id),
      ]);
      toast({ title: 'Team renamed', description: `Team name updated to "${editTeamName.trim()}"` });
      await refreshLeague();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setEditSaving(false);
      setEditTarget(null);
    }
  };

  const handleSendDm = async () => {
    if (!dmTarget || !dmContent.trim()) return;
    setDmSending(true);
    try {
      await sendDirectMessage(dmTarget.user_id, dmContent.trim());
      toast({ title: 'Message sent', description: `DM sent to ${dmTarget.display_name}` });
      setDmContent('');
      setDmTarget(null);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setDmSending(false);
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'commissioner':
        return (
          <Badge className="bg-gold/20 text-gold border-gold/30 text-xs">
            <Crown className="w-3 h-3 mr-1" />
            Commish
          </Badge>
        );
      case 'co-commissioner':
        return (
          <Badge className="bg-primary/20 text-primary border-primary/30 text-xs">
            <Shield className="w-3 h-3 mr-1" />
            Co-Commish
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Invite Section (Commissioner only) */}
      {isCommissioner && inviteCode && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Link2 className="w-4 h-4 text-primary" />
              Invite Members
            </CardTitle>
            <CardDescription>Share the invite code or link to add players</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="flex-1 rounded-md bg-muted/50 border border-border px-3 py-2 text-sm font-mono text-foreground truncate">
                {inviteCode}
              </div>
              <Button variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={handleCopyInvite}>
                <Copy className="w-3.5 h-3.5" />
                Copy Link
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Members List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="w-4 h-4 text-primary" />
              Members ({members.length})
            </CardTitle>
            {isCommissioner && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={handleEmailLeague} disabled={!emailsLoaded || memberEmails.size === 0}>
                <Mail className="w-3.5 h-3.5" />
                {!emailsLoaded ? 'Loading...' : 'Email League'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {sortedMembers.map((member) => {
            const isSelf = member.user_id === user?.id;
            const canManage = isCommissioner && member.role !== 'commissioner' && !isSelf;

            return (
              <div
                key={member.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/50"
              >
                {/* Avatar */}
                <div className={cn(
                  "w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0",
                  member.role === 'commissioner'
                    ? "bg-gradient-to-br from-gold to-amber-600 text-primary-foreground"
                    : member.role === 'co-commissioner'
                    ? "bg-gradient-sabotage text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                )}>
                  {(member.display_name || '?')[0].toUpperCase()}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm truncate">
                      {member.display_name}{isSelf && <span className="text-muted-foreground"> (you)</span>}
                    </p>
                    {getRoleBadge(member.role)}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{member.team_name || 'No Team'}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  {!isSelf && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-primary"
                      onClick={() => { setDmTarget(member); setDmContent(''); }}
                    >
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                  )}
                  {isCommissioner && !isSelf && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-primary"
                      onClick={() => handleEmailMember(member.user_id)}
                    >
                      <Mail className="w-4 h-4" />
                    </Button>
                  )}
                  {canManage && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {member.team_id && (
                          <DropdownMenuItem onClick={() => { setEditTarget(member); setEditTeamName(member.team_name || ''); }}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit Team Name
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => { setDmTarget(member); setDmContent(''); }}>
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Message
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setRemoveTarget(member)}
                          className="text-destructive focus:text-destructive"
                        >
                          <UserMinus className="w-4 h-4 mr-2" />
                          Remove from League
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            );
          })}

          {members.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">No members yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Remove Confirmation */}
      <AlertDialog open={!!removeTarget} onOpenChange={() => setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from League?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <strong>{removeTarget?.display_name}</strong> and their team from the league. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove} className="bg-destructive hover:bg-destructive/90">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Team Name Dialog */}
      <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team Name</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Team Name for {editTarget?.display_name}</Label>
            <Input
              value={editTeamName}
              onChange={e => setEditTeamName(e.target.value)}
              placeholder="Team name"
              maxLength={40}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={editSaving || !editTeamName.trim()}>
              {editSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DM Dialog */}
      <Dialog open={!!dmTarget} onOpenChange={() => setDmTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Message {dmTarget?.display_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Input
              value={dmContent}
              onChange={e => setDmContent(e.target.value)}
              placeholder="Type your message..."
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendDm(); } }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDmTarget(null)}>Cancel</Button>
            <Button onClick={handleSendDm} disabled={dmSending || !dmContent.trim()} className="gap-1.5">
              <Send className="w-3.5 h-3.5" />
              {dmSending ? 'Sending...' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MembersTab;
